// The things the desktop shell can be asked to do (TODO §14.7).
//
// The native File menu (§14.7.4) and the buttons on the start screen are two ways of asking
// for the same eight things, so they share one implementation. The menu dispatches into the
// renderer over IPC precisely so this can be true: the renderer owns the router, the store
// and the tab strip, and main never learns what a deck is.

import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { desktopShell } from "@/lib/desktopBridge";
import { DeckFileError, exportDeckFile, importDeckFile } from "@/lib/deckFile";
import { createNewDeck } from "@/store/bootstrap";
import {
  closeDesktopTab,
  cycleDesktopTab,
  getDesktopTabs,
} from "@/desktop/tabs";

/** The deck the window is showing: the route if it is a deck route, else the active tab. */
function deckIdFromPath(pathname: string): string | null {
  const match = /^\/d\/([^/]+)/.exec(pathname);
  return match ? decodeURIComponent(match[1]) : null;
}

export interface DesktopActions {
  newDeck: () => Promise<void>;
  newFromLibrary: () => Promise<void>;
  openDeckFile: () => Promise<void>;
  saveACopy: () => Promise<void>;
  closeTab: () => void;
  cycleTab: (delta: number) => void;
  openInNewWindow: () => void;
  showDocuments: () => void;
}

export function useDesktopActions(): DesktopActions {
  const navigate = useNavigate();
  const location = useLocation();

  const currentDeckId = useCallback(
    () => deckIdFromPath(location.pathname) ?? getDesktopTabs().activeDeckId,
    [location.pathname],
  );

  const newDeck = useCallback(async () => {
    navigate(`/d/${await createNewDeck()}`);
  }, [navigate]);

  // §14.7.3 With §14.2 landed the Library works offline too, so "New from template" is the
  // same entry point it is on the web — no bundled-starter-decks fallback is needed.
  const newFromLibrary = useCallback(async () => {
    navigate(`/d/${await createNewDeck()}?panel=library`);
  }, [navigate]);

  const openDeckFile = useCallback(async () => {
    const shell = desktopShell();
    if (!shell) return;
    try {
      const picked = await shell.openDeckFile();
      if (!picked) return; // The user cancelled the panel — not an error.
      // §12.6.2 Import always remaps ids into a NEW deck, so opening the same file twice
      // gives two documents rather than silently overwriting one.
      const file = new File([new Uint8Array(picked.data)], picked.name);
      const { deckId, adoptedNuggets } = await importDeckFile(file);
      // §17.5.3 The same sentence the documents home shows, for the same reason.
      toast.success(`Opened “${picked.name}”`, {
        description:
          adoptedNuggets > 0
            ? `${adoptedNuggets} nugget${adoptedNuggets === 1 ? "" : "s"} already on this device — linked to your copy rather than replacing it.`
            : undefined,
      });
      navigate(`/d/${deckId}`);
    } catch (err) {
      toast.error(
        err instanceof DeckFileError
          ? err.message
          : "That file couldn’t be opened",
      );
    }
  }, [navigate]);

  // §14.7.5 There is no Save. The document is already saved — this writes an explicit copy
  // to wherever the user points the save panel.
  const saveACopy = useCallback(async () => {
    const deckId = currentDeckId();
    if (!deckId) {
      toast.error("Open a document first");
      return;
    }
    if (!(await exportDeckFile(deckId)))
      toast.error("That deck is no longer here");
  }, [currentDeckId]);

  const closeTab = useCallback(() => {
    const deckId = currentDeckId();
    if (!deckId) return;
    const next = closeDesktopTab(deckId);
    navigate(next ? `/d/${next}` : "/", { replace: true });
  }, [currentDeckId, navigate]);

  const cycleTab = useCallback(
    (delta: number) => {
      const next = cycleDesktopTab(delta);
      if (next) navigate(`/d/${next}`);
    },
    [navigate],
  );

  // §14.7.2 A second BrowserWindow on the SAME origin, showing the same route. That is the
  // point: §12.5's Web Locks + BroadcastChannel coordination then applies unchanged, and the
  // second window opens read-only with "Edit here" rather than racing the first one's writes.
  const openInNewWindow = useCallback(() => {
    desktopShell()?.openWindow(location.pathname + location.search);
  }, [location.pathname, location.search]);

  const showDocuments = useCallback(() => navigate("/documents"), [navigate]);

  return {
    newDeck,
    newFromLibrary,
    openDeckFile,
    saveACopy,
    closeTab,
    cycleTab,
    openInNewWindow,
    showDocuments,
  };
}
